import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Bookmark, Download, Lock, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, errMsg } from "../lib/api";
import { fmtDate, fmtDateTime } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Empty, PageHeader, Pager, Td, Th } from "../components/common/UI";
import { ConfBadge, PriorityBadge, StatusBadge } from "../components/common/Badges";
import { Button } from "../components/ui/button";

const TITLES = { all: "All Measures", ordinances: "Ordinances", resolutions: "Resolutions", proposed: "Proposed Measures", amendments: "Amendments and Repeals" };
const FILTER_KEYS = ["q", "measure_type_id", "status", "stage_category", "term_id", "sponsor_id", "committee_id", "priority", "year", "numbered", "sort", "order"];

function Filters({ lk, params, set }) {
  const sel = (key, items, placeholder, valueKey = "id", labelKey = "name") => (
    <select data-testid={`filter-${key}`} className="otms-input" value={params.get(key) || ""} onChange={(e) => set(key, e.target.value)}>
      <option value="">{placeholder}</option>
      {items.map((i) => <option key={i[valueKey]} value={i[valueKey]}>{i[labelKey]}</option>)}
    </select>
  );
  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-8">
      {sel("measure_type_id", lk.measure_types, "All types")}
      {sel("status", lk.stages, "All statuses", "code", "name")}
      {sel("term_id", lk.terms, "All terms")}
      {sel("sponsor_id", lk.legislators, "All sponsors", "id", "full_name")}
      {sel("committee_id", lk.committees, "All committees")}
      {sel("priority", ["low", "normal", "high", "urgent"].map((p) => ({ id: p, name: p })), "Any priority")}
      <input data-testid="filter-year" className="otms-input" placeholder="Filing year" value={params.get("year") || ""} onChange={(e) => set("year", e.target.value.replace(/\D/g, "").slice(0, 4))} />
      <select data-testid="filter-sort" className="otms-input" value={`${params.get("sort") || "updated_at"}:${params.get("order") || "desc"}`}
        onChange={(e) => { const [s, o] = e.target.value.split(":"); set("sort", s, { order: o }); }}>
        <option value="updated_at:desc">Recently updated</option>
        <option value="filing_date:desc">Filing date (newest)</option>
        <option value="filing_date:asc">Filing date (oldest)</option>
        <option value="official_number:asc">Number (ascending)</option>
        <option value="proposed_title:asc">Title (A–Z)</option>
      </select>
    </div>
  );
}

function SavedSearches({ params, setParams }) {
  const [list, setList] = useState([]);
  const load = () => api.get("/saved-searches").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    const name = window.prompt("Name this saved search");
    if (!name) return;
    await api.post("/saved-searches", { name, params: Object.fromEntries(params.entries()) });
    toast.success("Search saved");
    load();
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button size="sm" variant="outline" onClick={save} data-testid="save-search-button"><Bookmark className="mr-1.5 h-3.5 w-3.5" />Save search</Button>
      {list.map((s) => (
        <span key={s.id} className="inline-flex items-center rounded-full border border-slate-200 bg-white text-xs">
          <button className="px-2.5 py-1 hover:text-sky-700" data-testid={`saved-search-${s.id}`} onClick={() => setParams(s.params)}>{s.name}</button>
          <button className="pr-2 text-slate-400 hover:text-red-600" onClick={() => api.delete(`/saved-searches/${s.id}`).then(load)}><X className="h-3 w-3" /></button>
        </span>
      ))}
    </div>
  );
}

export default function Registry() {
  const { view } = useParams();
  const [params, setParams] = useSearchParams();
  const { can } = useAuth();
  const nav = useNavigate();
  const [lk, setLk] = useState(null);
  const [data, setData] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  const page = Number(params.get("page") || 1);

  useEffect(() => { api.get("/lookups").then((r) => setLk(r.data)); }, []);
  const query = useCallback(() => {
    const p = { view: view === "all" ? undefined : view, page, page_size: 25 };
    FILTER_KEYS.forEach((k) => params.get(k) && (p[k] = params.get(k)));
    return p;
  }, [params, view, page]);
  useEffect(() => {
    setData(null);
    api.get("/measures", { params: query() }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [query]);

  const set = (key, value, extra = {}) => {
    const next = new URLSearchParams(params);
    [[key, value], ...Object.entries(extra)].forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    next.delete("page");
    setParams(next);
  };
  const exportAs = (format) =>
    downloadFile("/measures/export", { ...query(), format, page: undefined }, `otms-measures.${format}`).then(() => toast.success("Export ready")).catch((e) => toast.error(errMsg(e)));

  return (
    <div className="fade-up">
      <PageHeader eyebrow="Legislative Registry" title={TITLES[view] || "Measures"} subtitle="Search, filter and open authorized measures in this LGU."
        actions={<>
          {can("measure.export") && <>
            <Button size="sm" variant="outline" onClick={() => exportAs("csv")} data-testid="export-csv-button"><Download className="mr-1.5 h-3.5 w-3.5" />CSV</Button>
            <Button size="sm" variant="outline" onClick={() => exportAs("xlsx")} data-testid="export-xlsx-button"><Download className="mr-1.5 h-3.5 w-3.5" />Excel</Button>
          </>}
          {can("measure.create") && <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={() => nav("/measures/new")} data-testid="new-measure-button"><Plus className="mr-1.5 h-4 w-4" />New measure</Button>}
        </>} />
      <div className="mb-4 space-y-3 rounded-md border border-slate-200 bg-white p-4 shadow-sm">
        <form onSubmit={(e) => { e.preventDefault(); set("q", q); }} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input data-testid="measure-search-input" className="otms-input pl-9" placeholder="Search number, tracking reference, title, abstract or subject" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Button type="submit" size="sm" className="h-9 bg-slate-900" data-testid="measure-search-button">Search</Button>
          {[...params.keys()].length > 0 && <Button type="button" size="sm" variant="ghost" className="h-9" onClick={() => { setQ(""); setParams({}); }} data-testid="clear-filters-button">Clear</Button>}
        </form>
        {lk && <Filters lk={lk} params={params} set={set} />}
        {params.get("stage_category") && <div className="text-xs text-slate-500">Stage group: <b>{params.get("stage_category")}</b>{params.get("numbered") && <> · numbered: <b>{params.get("numbered")}</b></>}</div>}
        <SavedSearches params={params} setParams={setParams} />
      </div>
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[960px]" data-testid="measures-table">
          <thead><tr><Th>Reference / Number</Th><Th>Title</Th><Th>Type</Th><Th>Status</Th><Th>Sponsor</Th><Th>Committee</Th><Th>Filed</Th><Th>Last action</Th></tr></thead>
          <tbody>
            {data?.items.map((m) => (
              <tr key={m.id} className="cursor-pointer transition-colors hover:bg-sky-50/50" onClick={() => nav(`/measures/${m.id}`)} data-testid={`measure-row-${m.id}`}>
                <Td>
                  <div className="font-mono text-[11px] text-slate-500">{m.tracking_ref}</div>
                  {m.official_number && <div className="mt-0.5 font-medium text-slate-900">{m.official_number}</div>}
                </Td>
                <Td className="max-w-md">
                  <Link to={`/measures/${m.id}`} className="line-clamp-2 font-medium text-slate-800 hover:text-sky-700">{m.proposed_title}</Link>
                  <div className="mt-1 flex gap-1.5"><PriorityBadge value={m.priority} /><ConfBadge value={m.confidentiality} />{m.is_locked && <Lock className="h-3 w-3 text-slate-400" />}</div>
                </Td>
                <Td>{m.measure_type}</Td>
                <Td><StatusBadge category={m.stage_category} name={m.stage_name} /></Td>
                <Td>{m.sponsor || "—"}</Td>
                <Td>{m.committee || "—"}</Td>
                <Td>{fmtDate(m.filing_date)}</Td>
                <Td>{fmtDateTime(m.last_action_at)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
        {!data && <div className="py-10 text-center text-sm text-slate-400">Loading…</div>}
        {data && data.items.length === 0 && <Empty text="No measures match these filters." />}
      </div>
      <Pager meta={data} onPage={(p) => { const n = new URLSearchParams(params); n.set("page", p); setParams(n); }} />
    </div>
  );
}
